import { useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  PricebookBranch,
  PricebookBranches,
} from "../../../../types/price-book.type";
import { numberWithCommas } from "../../../../utils";
import {
  CATALOGUE_CELL_PADDING,
  STRIPE_BACKGROUND,
} from "../../shared/constants";
import CustomerListModal from "./CustomerListModal";

const COLUMNS = ["สาขา", "ร้านค้าที่ผูก", ""];

/** The branch whose shops the modal lists; no branch = every branch the reader may see. */
interface OpenShopList {
  branchUuid?: string;
  branchLabel?: string;
}

const formatBranchLabel = (branch: PricebookBranch): string =>
  `${branch.branch_code} ${branch.branch_name}`;

interface BranchShopsTabProps {
  pricebookUuid: string;
  branches: PricebookBranches | null;
  loading: boolean;
  /** Shops on the book in every branch the reader may see. */
  shopCount: number;
}

/**
 * ร้านค้าตามสาขา: the branches the book is open in, with the shops each has on it. Binding a
 * shop to a book stays on the customer page; this tab only reads and opens the lists.
 */
export default function BranchShopsTab({
  pricebookUuid,
  branches,
  loading,
  shopCount,
}: BranchShopsTabProps) {
  const [shopList, setShopList] = useState<OpenShopList | null>(null);

  // A branch with shops on the book is listed even if it is no longer open there.
  const listedBranches = (branches?.branches ?? []).filter(
    (branch) => branch.is_selected || branch.shop_count > 0,
  );
  const emptyMessage = loading ? "กำลังโหลดสาขา..." : "ไม่พบสาขาที่ใช้สมุดราคานี้";

  const handleOpenAll = () => setShopList({});
  const handleOpenBranch = (branch: PricebookBranch) =>
    setShopList({
      branchUuid: branch.branch_uuid,
      branchLabel: formatBranchLabel(branch),
    });
  const handleCloseList = () => setShopList(null);

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
          ร้านค้าที่ใช้สมุดราคานี้ {numberWithCommas(shopCount)} ราย
        </Typography>
        <Button size="small" variant="outlined" onClick={handleOpenAll}>
          ดูร้านค้าทั้งหมด
        </Button>
      </Stack>

      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {listedBranches.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {listedBranches.map((branch, index) => (
              <BranchShopRow
                key={branch.company_id}
                branch={branch}
                striped={index % 2 === 1}
                onOpen={handleOpenBranch}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary">
        การผูกสมุดราคาให้ลูกค้า ทำที่หน้ารายละเอียดลูกค้า
      </Typography>

      <CustomerListModal
        open={shopList !== null}
        priceBookUuid={pricebookUuid}
        branchUuid={shopList?.branchUuid}
        branchLabel={shopList?.branchLabel}
        onClose={handleCloseList}
      />
    </Stack>
  );
}

interface BranchShopRowProps {
  branch: PricebookBranch;
  striped: boolean;
  onOpen: (branch: PricebookBranch) => void;
}

function BranchShopRow({ branch, striped, onOpen }: BranchShopRowProps) {
  const handleOpen = () => onOpen(branch);

  return (
    <TableRow
      sx={{
        bgcolor: (theme) =>
          striped ? STRIPE_BACKGROUND : theme.palette.background.paper,
        "& td": { padding: `${CATALOGUE_CELL_PADDING}px` },
      }}
    >
      <TableCell>{formatBranchLabel(branch)}</TableCell>
      <TableCell>{numberWithCommas(branch.shop_count)} ร้าน</TableCell>
      <TableCell align="right">
        <Button size="small" disabled={branch.shop_count === 0} onClick={handleOpen}>
          ดูร้านค้า
        </Button>
      </TableCell>
    </TableRow>
  );
}
